// Utility functions for calculating service plan costs
// Can be used by both ServiceBuilderSection and SupportAtHomeSuggestionEngine

import { SUPPORT_AT_HOME_SERVICES } from './src/features/supportAtHome/supportAtHomeServices.tsx';
import type { ServiceBuilderSelections, ServiceBuilderContributionRates } from './src/features/supportAtHome/ServiceBuilderSection.tsx';

// Convert frequency to monthly for calculations
export const convertToMonthly = (quantity: number, freqType: string): number => {
    if (!quantity || quantity <= 0) return 0;
    switch(freqType) {
        case 'daily': return quantity * 30; // Approximate monthly from daily
        case 'weekly': return quantity * 4; // Approximate monthly from weekly
        case 'monthly': return quantity;
        default: return quantity;
    }
};

export interface ServicePlanCosts {
    totalAnnual: number;
    clinical: number;
    independence: number;
    everyday: number;
    hasServicePlan: boolean;
    contribution: {
        clinical: number;
        independence: number;
        everyday: number;
        total: number;
    };
}

// Calculate service costs from selectedServices
export const calculateServicePlanCosts = (
    selectedServices: ServiceBuilderSelections | null | undefined,
    contributionRates: ServiceBuilderContributionRates = {},
    isPartnered = false,
): ServicePlanCosts => {
    if (!selectedServices || (!selectedServices.client && !selectedServices.partner)) {
        return {
            totalAnnual: 0,
            clinical: 0,
            independence: 0,
            everyday: 0,
            hasServicePlan: false,
            contribution: {
                clinical: 0,
                independence: 0,
                everyday: 0,
                total: 0
            }
        };
    }

    let totalClinical = 0;
    let totalIndependence = 0;
    let totalEveryday = 0;

    // Calculate for both client and partner if partnered
    const personsToCalculate: ('client' | 'partner')[] = isPartnered ? ['client', 'partner'] : ['client'];

    personsToCalculate.forEach(person => {
        const personServices = selectedServices[person] || {};

        Object.entries(personServices).forEach(([serviceId, data]) => {
            if (!data || !data.quantity || Number(data.quantity) <= 0) return;

            const monthlyQuantity = convertToMonthly(Number(data.quantity), data.frequency || 'monthly');

            // Find service in each category
            Object.values(SUPPORT_AT_HOME_SERVICES).forEach(category => {
                const service = category.services.find(s => s.id === serviceId);
                if (service) {
                    const cost = service.medianPrice * monthlyQuantity * 12; // Convert to annual
                    if (category.label === 'Clinical Care') totalClinical += cost;
                    else if (category.label === 'Independence Services') totalIndependence += cost;
                    else if (category.label === 'Everyday Living Services') totalEveryday += cost;
                }
            });
        });
    });

    const total = totalClinical + totalIndependence + totalEveryday;

    // Calculate contribution amounts
    const clinicalContribution = 0; // Clinical is 0% contribution
    const independenceContribution = totalIndependence * (contributionRates.independence || 0);
    const everydayContribution = totalEveryday * (contributionRates.everydayLiving || 0);
    const uncappedContribution = clinicalContribution + independenceContribution + everydayContribution;
    // The no-worse-off fortnightly cap, carried on the rates (supportAtHomeContributionEngine): a year's
    // contributions never exceed the previous income-tested care fee. Scaled across the categories.
    // `null`/absent means no ceiling — Number(null) is 0, which is not "no cap", so test for presence first.
    const annualCap = contributionRates.annualCap == null ? null : Number(contributionRates.annualCap);
    const capScale = annualCap != null && Number.isFinite(annualCap) && annualCap >= 0 && uncappedContribution > annualCap ? annualCap / uncappedContribution : 1;
    const totalContribution = uncappedContribution * capScale;

    return {
        totalAnnual: total,
        clinical: totalClinical,
        independence: totalIndependence,
        everyday: totalEveryday,
        hasServicePlan: total > 0,
        contribution: {
            clinical: 0,
            independence: independenceContribution * capScale,
            everyday: everydayContribution * capScale,
            total: totalContribution
        }
    };
};

/** Restored-from-storage shape; looser than ServiceBuilderSelections since it comes from JSON.parse. */
export interface StoredServicePlan {
    client: Record<string, unknown>;
    partner: Record<string, unknown>;
    [key: string]: unknown;
}

interface StoredStrategyLike {
    type?: string;
    createdAt?: string | number;
    inputs?: {
        services?: {
            client?: unknown;
            partner?: unknown;
            [key: string]: unknown;
        };
    };
}

const CURRENT_PLAN_KEY = 'careCompanionCurrentServicePlan';
const CURRENT_PLAN_META_KEY = 'careCompanionCurrentServicePlanMeta';
/**
 * Set when the reader CHOSE to model with no service plan (owner-caught 2026-08-27, and again
 * 2026-09-24). Clearing the current plan alone was not a choice the resolution could see: with the
 * current plan gone it fell straight through to the newest saved plan, so "No service plan" snapped
 * back to "Service Plan A" the moment it was picked — and it could only ever be picked when a saved
 * plan existed, which is exactly when the fallback fires. The flag outranks every fallback until a
 * plan is loaded or built again.
 */
const NO_PLAN_KEY = 'careCompanionNoServicePlan';

/** A {client, partner} bag — as opposed to a flat map of services. */
const looksLikeCoupleBag = (v: unknown): v is { client?: Record<string, unknown>; partner?: Record<string, unknown> } =>
    Boolean(v) && typeof v === 'object' && ('client' in (v as object) || 'partner' in (v as object));

/**
 * Normalise a stored services bag (couple {client,partner} or flat single map) to couple shape.
 *
 * Also unwraps the DOUBLE-WRAPPED shape every single client's plan was saved in until 2026-09-13 —
 * `{ client: { client: {...}, partner: {} }, partner: null }` — which the builder produced by
 * wrapping its already-{client, partner} state a second time. Those records exist in saved
 * sessions and on the server, so they are read correctly rather than re-saved.
 */
export const normaliseStoredServices = (services: unknown): StoredServicePlan => {
    if (!services || typeof services !== 'object') return { client: {}, partner: {} };
    const bag = services as { client?: Record<string, unknown>; partner?: Record<string, unknown> };
    if (bag.client || bag.partner) {
        if (looksLikeCoupleBag(bag.client)) {
            return { client: bag.client.client || {}, partner: bag.client.partner || bag.partner || {} };
        }
        return { client: bag.client || {}, partner: bag.partner || {} };
    }
    return { client: services as Record<string, unknown>, partner: {} };
};

/** Every saved home-care strategy that carries services - for pickers. Newest first. */
export const listSavedServicePlans = (): Array<{ id: string; name: string; plan: StoredServicePlan }> => {
    try {
        if (typeof window === 'undefined') return [];
        const stored = sessionStorage.getItem('careCompanionStrategies');
        if (!stored) return [];
        const parsed = JSON.parse(stored);
        if (!Array.isArray(parsed)) return [];
        return parsed
            .filter((s: StoredStrategyLike & { id?: string; name?: string }) => s?.type === 'home_care' && s?.inputs?.services)
            .sort((a: StoredStrategyLike, b: StoredStrategyLike) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())
            .map((s: StoredStrategyLike & { id?: string; name?: string }) => ({
                id: String(s.id ?? ''),
                name: String(s.name || 'saved plan'),
                plan: normaliseStoredServices(s.inputs?.services),
            }));
    } catch {
        return [];
    }
};

/** A stored plan with at least one service at a quantity above zero — the only kind that prices. */
export const storedPlanHasServices = (plan: StoredServicePlan | null | undefined): boolean =>
    (['client', 'partner'] as const).some((side) =>
        Object.values((plan?.[side] as Record<string, { quantity?: unknown } | undefined> | undefined) || {})
            .some((d) => Number(d?.quantity) > 0),
    );

/**
 * The saved plans a picker may offer: those with services in them.
 *
 * A plan saved with a name and nothing ticked is a real record (owner-caught, 2026-09-13: "Test
 * Plan" kept snapping the picker back to "no service plan"). Picking it made it the current plan,
 * the figures found nothing to price and fell back to the illustrative package, and the control —
 * which shows what is actually in use — reverted. Nothing was wrong except that the option should
 * never have been offered.
 */
export const usableSavedServicePlans = <T extends { plan: StoredServicePlan }>(plans: T[]): T[] =>
    plans.filter((p) => storedPlanHasServices(p.plan));

/**
 * The record the Service Builder saves for a named plan — built here, beside the reader, so the
 * two cannot disagree about the shape. `services` is the builder's {client, partner} state as it
 * stands; it is never wrapped again (see normaliseStoredServices for what that did).
 */
export const buildServicePlanRecord = (input: {
    name: string;
    services: StoredServicePlan;
    outputs: { totalCost: number; contribution: number; legacyEstimate: unknown };
}) => ({
    id: `strategy-${Date.now()}`,
    name: input.name.trim(),
    type: 'home_care' as const,
    inputs: { services: { client: input.services.client || {}, partner: input.services.partner || {} } },
    outputs: input.outputs,
    createdAt: new Date().toISOString(),
});

/** Make `plan` the current working plan, remembering which saved plan it came from (if any). */
export const setCurrentServicePlan = (plan: StoredServicePlan, basedOnName?: string | null): void => {
    if (typeof window === 'undefined') return;
    try {
        sessionStorage.setItem(CURRENT_PLAN_KEY, JSON.stringify(plan));
        sessionStorage.removeItem(NO_PLAN_KEY);
        if (basedOnName) sessionStorage.setItem(CURRENT_PLAN_META_KEY, JSON.stringify({ basedOnName }));
        else sessionStorage.removeItem(CURRENT_PLAN_META_KEY);
    } catch { /* storage unavailable - figures still work off in-memory state */ }
};

/**
 * Put the module back to having NO service plan — the client's current position.
 *
 * Distinct from clearCurrentServicePlanMeta, which only forgets which saved plan the selections
 * came from and leaves the selections themselves in place. This removes both, so the figures fall
 * back to the illustrative package and the panel says "your position today" again.
 *
 * Added because the picker could load a plan but never let go of one (owner-caught, 2026-08-27):
 * having chosen a plan there was no way back to modelling without one, which is a real state — it
 * is what someone sees before they build anything, and they may well want to return to it.
 */
export const clearCurrentServicePlan = (): void => {
    if (typeof window === 'undefined') return;
    try {
        sessionStorage.removeItem(CURRENT_PLAN_KEY);
        sessionStorage.removeItem(CURRENT_PLAN_META_KEY);
        // The choice itself, so the saved-plan and profile fallbacks do not quietly undo it.
        sessionStorage.setItem(NO_PLAN_KEY, '1');
    } catch { /* storage unavailable - figures still work off in-memory state */ }
};

/**
 * The reader has started choosing services again, so "no service plan" no longer describes what
 * they want. The Service Builder calls this beside its own write of the current plan.
 */
export const clearNoServicePlanChoice = (): void => {
    if (typeof window === 'undefined') return;
    try { sessionStorage.removeItem(NO_PLAN_KEY); } catch { /* ignore */ }
};

/** The current selections have diverged from the plan they were loaded from. */
export const clearCurrentServicePlanMeta = (): void => {
    if (typeof window === 'undefined') return;
    try { sessionStorage.removeItem(CURRENT_PLAN_META_KEY); } catch { /* ignore */ }
};

/** Where a resolved service plan came from - so the UI can NAME it, not just use it. */
export type ServicePlanSource =
    | { kind: 'current'; basedOnName?: string }
    | { kind: 'saved'; name: string; savedCount: number }
    | { kind: 'restore'; name: string }
    /** `chosen`: the reader picked "No service plan" — not merely that nothing was found. */
    | { kind: 'none'; chosen?: boolean };

/**
 * The same resolution as getServicePlanFromStorage, WITH provenance (owner ask, 2026-08-25: the
 * Optimise tab said "the plan built in Service Builder" while the builder read "Baseline" - which
 * is its FINANCIAL POSITION dropdown, a different axis from which services are selected. Naming
 * the source is what untangles the two for the reader).
 */
export const getServicePlanWithSource = (): { plan: StoredServicePlan; source: ServicePlanSource } => {
    const none: { plan: StoredServicePlan; source: ServicePlanSource } = { plan: { client: {}, partner: {} }, source: { kind: 'none' } };
    try {
        if (typeof window === 'undefined') return none;

        const currentPlan = sessionStorage.getItem(CURRENT_PLAN_KEY);
        if (currentPlan) {
            const parsed = JSON.parse(currentPlan);
            if (parsed && (parsed.client || parsed.partner)) {
                let basedOnName: string | undefined;
                try {
                    const meta = sessionStorage.getItem(CURRENT_PLAN_META_KEY);
                    basedOnName = meta ? String(JSON.parse(meta)?.basedOnName || '') || undefined : undefined;
                } catch { basedOnName = undefined; }
                return { plan: parsed, source: { kind: 'current', basedOnName } };
            }
        }

        // Chosen none: said so, and nothing below is consulted.
        if (sessionStorage.getItem(NO_PLAN_KEY)) return { ...none, source: { kind: 'none', chosen: true } };

        const stored = sessionStorage.getItem('careCompanionStrategies');
        if (stored) {
            const parsed = JSON.parse(stored);
            const servicePlans: (StoredStrategyLike & { name?: string })[] = Array.isArray(parsed)
                ? parsed.filter((strategy: StoredStrategyLike) => strategy?.type === 'home_care' && strategy?.inputs?.services)
                : [];
            if (servicePlans.length > 0) {
                const mostRecent = servicePlans.sort((a, b) =>
                    new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
                )[0];
                if (mostRecent.inputs?.services) {
                    const source: ServicePlanSource = {
                        kind: 'saved',
                        name: String(mostRecent.name || 'saved plan'),
                        savedCount: servicePlans.length,
                    };
                    if (mostRecent.inputs.services.client || mostRecent.inputs.services.partner) {
                        return { plan: mostRecent.inputs.services as StoredServicePlan, source };
                    }
                    return { plan: { client: mostRecent.inputs.services as Record<string, unknown>, partner: {} }, source };
                }
            }
        }

        const restorePlan = sessionStorage.getItem('careCompanionRestoreServicePlan');
        if (restorePlan) {
            const strategy: StoredStrategyLike & { name?: string } = JSON.parse(restorePlan);
            if (strategy?.inputs?.services) {
                const source: ServicePlanSource = { kind: 'restore', name: String(strategy.name || 'reopened plan') };
                if (strategy.inputs.services.client || strategy.inputs.services.partner) {
                    return { plan: strategy.inputs.services as StoredServicePlan, source };
                }
                return { plan: { client: strategy.inputs.services as Record<string, unknown>, partner: {} }, source };
            }
        }
    } catch {
        return none;
    }
    return none;
};

// Get service plan from sessionStorage (stored by ServiceBuilderSection)
export const getServicePlanFromStorage = (): StoredServicePlan => {
    try {
        if (typeof window === 'undefined') return { client: {}, partner: {} };

        // First, try to get current service plan (most up-to-date)
        const currentPlan = sessionStorage.getItem('careCompanionCurrentServicePlan');
        if (currentPlan) {
            const parsed = JSON.parse(currentPlan);
            if (parsed && (parsed.client || parsed.partner)) {
                return parsed;
            }
        }

        // The reader chose "no service plan": that is the answer (see getServicePlanWithSource).
        if (sessionStorage.getItem(NO_PLAN_KEY)) return { client: {}, partner: {} };

        // Try to get from saved strategies (most recent)
        const stored = sessionStorage.getItem('careCompanionStrategies');
        if (stored) {
            const parsed = JSON.parse(stored);
            const servicePlans: StoredStrategyLike[] = Array.isArray(parsed)
                ? parsed.filter((strategy: StoredStrategyLike) => strategy?.type === 'home_care' && strategy?.inputs?.services)
                : [];

            // Get the most recent service plan
            if (servicePlans.length > 0) {
                const mostRecent = servicePlans.sort((a, b) =>
                    new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
                )[0];

                if (mostRecent.inputs?.services) {
                    // Handle both single and couple formats
                    if (mostRecent.inputs.services.client || mostRecent.inputs.services.partner) {
                        return mostRecent.inputs.services as StoredServicePlan;
                    } else {
                        return { client: mostRecent.inputs.services as Record<string, unknown>, partner: {} };
                    }
                }
            }
        }

        // Fallback: try to get from restore service plan
        const restorePlan = sessionStorage.getItem('careCompanionRestoreServicePlan');
        if (restorePlan) {
            const strategy: StoredStrategyLike = JSON.parse(restorePlan);
            if (strategy?.inputs?.services) {
                if (strategy.inputs.services.client || strategy.inputs.services.partner) {
                    return strategy.inputs.services as StoredServicePlan;
                } else {
                    return { client: strategy.inputs.services as Record<string, unknown>, partner: {} };
                }
            }
        }
    } catch (error) {
        console.error('Error getting service plan from storage:', error);
    }

    return { client: {}, partner: {} };
};
